import AssignedInterviewReviewsManager from '@/components/AssignedInterviewReviewsManager';
import ChallengeTypeInput, { ChallengeTypeInputProps } from '@/components/ChallengeTypeInput';
import ChooseInterviewTypeModal, {
  ChooseInterviewTypeModalType,
} from '@/components/ChooseInterviewTypeModal';
import DialogBox from '@/components/DialogBox';
import EmptyInterview from '@/components/EmptyInterview';
import InterviewBox, { InterviewBoxType } from '@/components/InterviewBox';
import ConfirmationModal from '@/components/InterviewConfirmationModal';
import InterviewNotesManager from '@/components/InterviewNotesManager';
import InterviewQuestionsManager from '@/components/InterviewQuestionsManager';
import InterviewReviewsManager from '@/components/InterviewReviewsManager';
import SendConfirmationModal from '@/components/SendConfirmationModal';
import { TableOfContentSidebarType } from '@/components/TableOfContentSidebar';
import TechnicalValidationProject from '@/components/TechnicalValidationProject';
import TechnicalValidationReviewsManager from '@/components/TechnicalValidationReviewsManager';
import UpdateCandidateStatus from '@/components/UpdateCandidateStatus';
import { Candidate } from '@/services/candidates';
import { InterviewType } from '@/types/api';
import { interviewsThatShouldBeSent, InterviewTypes } from '@/utils/interviews';
import {
  AiGeneratedOverview,
  EditingInterviewType,
  InterviewStateType,
} from './CandidateInterviews.container';
import * as Styles from './CandidateInterviews.styles';

type InterviewFormattedType = InterviewType & {
  shouldDisplayChallengeProject: boolean;
  shouldDisplayTechnicalValidationReviewsManager: boolean;
  shouldDisplayQuestionsManager: boolean;
  shouldDisplayReviewsManager: boolean;
  shouldDisplayAssignedInterviewReviewsManager: boolean;
  shouldDisplayNotesManager: boolean;
  shouldDisplayChallengeTypeInput: boolean;
  canAddNewReview: boolean;
  canSubmitInterview: boolean;
  hasReviews: boolean;
  isCandidateStatusUpdated: boolean;
};

type CandidateInfoComponentProps = Required<
  Pick<TableOfContentSidebarType, 'tableOfContentOptions' | 'onClickAddNew'>
> &
  Omit<
    InterviewBoxType,
    'candidate' | 'selectInterviewToDelete' | 'onEditInterviewName' | 'interview' | 'children'
  > &
  Pick<ChooseInterviewTypeModalType, 'onSelectInterviewType'> &
  Pick<ChallengeTypeInputProps, 'languageUsedByCandidate' | 'onChangeLanguageUsed'> & {
    candidate?: Candidate;
    interviewType: InterviewStateType;
    messageToConfirmSending?: string;
    aiGeneratedOverview?: AiGeneratedOverview;
    onCancelNewInterview: () => void;
    onSelectInterviewName: (name: string) => void;
    interviews: InterviewFormattedType[];
    openDeleteDialog: boolean;
    openSendDialog: boolean;
    onSubmitInterview: () => void;
    onCreateInterview: (id: string, onCreateSuccess?: () => void) => void;
    selectInterviewToSend: (id: string, type: InterviewTypes) => void;
    selectInterviewToDelete: (id: string) => void;
    onDeleteInterview: () => void;
    onEditInterviewName: (name: string, id: string) => void;
    onUpdateInterview: (name: string) => void;
    onRefetchInterview: () => void;
    editingInterview: EditingInterviewType;
    isSendingInterview: boolean;
    onChooseChallengeType: (event: any) => void;
    technicalValidationTypeToSend: string;
    expandedInterviews: Record<string, boolean>;
    onToggleCollapseInterview: (interviewId: string, isOpen: boolean) => void;
    autoScrollInterview?: string;
    onAutoScroll?: () => void;
  };

const CandidateInfoComponent = function ({
  candidate,
  onRefetchInterview,
  onClickAddNew,
  interviewType,
  onSelectInterviewType,
  onSelectInterviewName,
  onCancelNewInterview,
  interviews,
  selectInterviewToDelete,
  openDeleteDialog,
  onDeleteInterview,
  onEditInterviewName,
  onUpdateInterview,
  editingInterview,
  onChangeEmailAddress,
  tableOfContentOptions,
  onSubmitInterview,
  selectInterviewToSend,
  onCreateInterview,
  openSendDialog,
  messageToConfirmSending,
  isSendingInterview,
  onChooseChallengeType,
  technicalValidationTypeToSend,
  expandedInterviews,
  onToggleCollapseInterview,
  autoScrollInterview,
  onAutoScroll,
  languageUsedByCandidate,
  onChangeLanguageUsed,
  aiGeneratedOverview,
}: CandidateInfoComponentProps): JSX.Element {
  const technicalInterviews = interviews.filter(
    (interview) => interview.type === 'TECHNICAL_CHALLENGE'
  );

  const lastTechnicalInterviews = technicalInterviews.slice(-1)[0];

  return (
    <div data-testid="candidate-interviews-page">
      <ChooseInterviewTypeModal
        onSelectInterviewType={onSelectInterviewType}
        onClose={onCancelNewInterview}
        isOpen={interviewType.openedModal === 'TYPE'}
      />

      <ConfirmationModal
        label="Give a title to your Interview"
        defaultName={interviewType.name}
        onConfirmClick={onSelectInterviewName}
        onCloseClick={onCancelNewInterview}
        isOpen={interviewType.openedModal === 'NAME'}
      />

      <ConfirmationModal
        label="Enter the new title of the interview"
        defaultName={editingInterview?.name ?? ''}
        onConfirmClick={onUpdateInterview}
        onCloseClick={() => onEditInterviewName('', '')}
        isOpen={!!editingInterview?.id}
      />

      <DialogBox
        isSubmitting={false}
        isOpen={openDeleteDialog}
        onCancelHandler={() => selectInterviewToDelete('')}
        onConfirmHandler={onDeleteInterview}
      />

      {aiGeneratedOverview && (
        <Styles.AIOverviewContainer data-testid="ai-generated-overview">
          <Styles.AIOverviewHeader>
            <Styles.AIOverviewTitle>AI Generated Overview</Styles.AIOverviewTitle>
            <Styles.AIOverviewMeta>
              {aiGeneratedOverview.interviewTitle}
              {aiGeneratedOverview.reviewerName ? ` • ${aiGeneratedOverview.reviewerName}` : ''}
            </Styles.AIOverviewMeta>
          </Styles.AIOverviewHeader>
          <Styles.AIOverviewText>{aiGeneratedOverview.overview}</Styles.AIOverviewText>
        </Styles.AIOverviewContainer>
      )}

      {!interviews.length && <EmptyInterview onClick={onClickAddNew} />}

      {!!interviews.length && candidate && (
        <Styles.PageGrid>
          <Styles.Sidebar
            tableOfContentOptions={tableOfContentOptions}
            onClickAddNew={onClickAddNew}
          />

          <Styles.Content>
            {interviews.map((interview) => {
              return (
                <InterviewBox
                  key={interview.id}
                  onChangeEmailAddress={onChangeEmailAddress}
                  onEditInterviewName={() => onEditInterviewName(interview.title, interview.id)}
                  selectInterviewToDelete={() => selectInterviewToDelete(interview.id)}
                  interview={interview}
                  candidate={candidate}
                  onSubmitInterview={selectInterviewToSend}
                  onCreateInterview={onCreateInterview}
                  isSubmitButtonDisabled={isSendingInterview || !interview.canSubmitInterview}
                  isAlreadySent={interview.isAlreadySent}
                  hideCandidateDetails={!interviewsThatShouldBeSent.includes(interview.type)}
                  hideSubmitButton={!interviewsThatShouldBeSent.includes(interview.type)}
                  isCollapsible
                  isOpen={expandedInterviews[interview.id]}
                  onToggleOpen={(value) => onToggleCollapseInterview(interview.id, value)}
                  onRefetchInterview={onRefetchInterview}
                  autoScroll={interview.id === autoScrollInterview}
                  onAutoScroll={onAutoScroll}
                >
                  <>
                    {interview.shouldDisplayChallengeProject && (
                      <TechnicalValidationProject
                        createdAt={interview.technicalChallenge?.createdAt}
                        technicalChallengeId={interview.technicalChallenge?.id}
                        gitlabProjectUrl={interview.technicalChallenge?.gitlabProjectUrl}
                        githubProjectUrl={interview.technicalChallenge?.githubProjectUrl}
                        technicalDesignTitle={interview.technicalChallenge?.technicalDocumentTitle}
                        technicalDesignContent={
                          interview.technicalChallenge?.technicalDocumentContent
                        }
                        videoUrl={interview.technicalChallenge?.technicalVideoUrl}
                        onRefetchInterview={onRefetchInterview}
                      />
                    )}

                    {interview.shouldDisplayQuestionsManager && (
                      <InterviewQuestionsManager
                        onRefetchInterview={onRefetchInterview}
                        interview={interview}
                        canManageQuestions={!interview.isAlreadySent}
                      />
                    )}

                    {interview.shouldDisplayChallengeTypeInput && (
                      <ChallengeTypeInput
                        value={technicalValidationTypeToSend}
                        onChangeDropDown={onChooseChallengeType}
                        languageUsedByCandidate={languageUsedByCandidate}
                        onChangeLanguageUsed={onChangeLanguageUsed}
                      />
                    )}

                    <Styles.NotesAndReviewBox
                      hasReviews={!!interview.reviews.length}
                      isScreeningInterview={interview.type === InterviewTypes.SCREENING}
                    >
                      {interview.shouldDisplayNotesManager && (
                        <InterviewNotesManager
                          interview={interview}
                          onRefetchInterview={onRefetchInterview}
                        />
                      )}
                      {interview.shouldDisplayAssignedInterviewReviewsManager && (
                        <>
                          <Styles.Divider />
                          <AssignedInterviewReviewsManager
                            isTeamInterview={interview.type === 'TEAM_INTERVIEW'}
                            teamInterviewId={
                              interview.type === 'TEAM_INTERVIEW' ? interview.id : ''
                            }
                            selectedTechnicalChallengeId={
                              interview?.technicalChallenge?.technicalChallenge?.id ??
                              lastTechnicalInterviews?.technicalChallenge?.technicalChallenge?.id ??
                              ''
                            }
                            interviewTechnicalChallengeId={interview.technicalChallenge?.id ?? ''}
                          />
                        </>
                      )}

                      {interview.shouldDisplayReviewsManager && (
                        <>
                          <Styles.Divider />

                          <InterviewReviewsManager
                            interview={interview}
                            canAddNewReview={interview.canAddNewReview}
                            onRefetchInterview={onRefetchInterview}
                          />
                        </>
                      )}
                    </Styles.NotesAndReviewBox>

                    {interview.shouldDisplayTechnicalValidationReviewsManager && (
                      <>
                        <Styles.Divider />

                        <TechnicalValidationReviewsManager
                          interview={interview}
                          onRefetchInterview={onRefetchInterview}
                          canAddFirstReview={
                            interview.shouldDisplayChallengeProject && !interview.hasReviews
                          }
                        />
                      </>
                    )}

                    {interview.hasReviews && (
                      <UpdateCandidateStatus
                        onRefetchInterview={onRefetchInterview}
                        isDisabled={!interview.hasReviews}
                        isAlreadyUpdated={interview.isCandidateStatusUpdated}
                        currentStatus={interview.person.status || undefined}
                      />
                    )}
                  </>
                </InterviewBox>
              );
            })}
          </Styles.Content>
        </Styles.PageGrid>
      )}

      <SendConfirmationModal
        onClose={() => selectInterviewToSend('', InterviewTypes.PRE_SCREENING)}
        actionMessage={messageToConfirmSending ?? ''}
        onConfirmHandler={onSubmitInterview}
        isOpen={openSendDialog}
        isButtonDisabled={isSendingInterview}
      />
    </div>
  );
};

export default CandidateInfoComponent;
